import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import DateRange from './components/DateRange';
import KPI from './components/KPI';
import { SMART_COLORS as COLORS, smartCardSx as cardSx } from '../../theme/smartStableTheme';
import { formatDateDisplay, formatDateInput } from '../../utils/dateUtils';

const SERVICE_META = {
  vetclinic: { labelKey: 'vetclinic', fallback: 'العيادة' },
  farrier: { labelKey: 'farrier', fallback: 'البيطار' },
  transportation: { labelKey: 'transport', fallback: 'النقل' },
  accommodation: { labelKey: 'accommodation', fallback: 'السكن' },
  reproduction: { labelKey: 'reproduction_clinic', fallback: 'عيادة الإنتاج' },
  competitions: { labelKey: 'competitions', fallback: 'Competitions' },
};

const moneyFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const smallMoneyFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const intFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});

const fmtInt = (value) => intFormatter.format(Number(value || 0));

function fmtMoney(value) {
  const n = Number(value || 0);
  if (Math.abs(n) < 1000 && !Number.isInteger(n)) {
    return smallMoneyFormatter.format(n);
  }
  return moneyFormatter.format(Math.round(n));
}

function fmtCompactMoney(value, t) {
  const n = Number(value || 0);
  const abs = Math.abs(n);
  if (abs >= 1000000) {
    return `${smallMoneyFormatter.format(n / 1000000)} ${t('million_short') || 'M'}`;
  }
  if (abs >= 100000) {
    return `${smallMoneyFormatter.format(n / 1000)} ${t('thousand_short') || 'K'}`;
  }
  return fmtMoney(n);
}

function currencyLabel(t) {
  return t('currency_sar') || 'SAR';
}

function toYMD(date) {
  return formatDateInput(date);
}

function getPresetRange(preset) {
  const now = new Date();
  const today = toYMD(now);

  if (preset === 'today') return { from: today, to: today };

  if (preset === 'last30') {
    const from = new Date(now);
    from.setDate(from.getDate() - 29);
    return { from: toYMD(from), to: today };
  }

  if (preset === 'year') {
    return { from: formatDateInput(new Date(now.getFullYear(), 0, 1)), to: today };
  }

  return {
    from: formatDateInput(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: today,
  };
}

function normalizeServiceName(service) {
  const s = String(service || '')
    .trim()
    .toLowerCase()
    .replace(/[_\s-]/g, '');

  if (['vet', 'clinic', 'vetclinic'].includes(s)) return 'vetclinic';
  if (['farrier', 'ferrier'].includes(s)) return 'farrier';
  if (['transport', 'transportation', 'horsetransport'].includes(s)) {
    return 'transportation';
  }
  if (['accommodation', 'accommodations', 'housing', 'stay'].includes(s)) {
    return 'accommodation';
  }
  if (['reproduction', 'reproductionclinic', 'productionclinic', 'breeding'].includes(s)) {
    return 'reproduction';
  }
  if (['competitions', 'competition', 'horseshows', 'show'].includes(s)) {
    return 'competitions';
  }
  return service || 'unknown';
}

function normalizeTotals(data) {
  const rows = Array.isArray(data?.totals) ? data.totals : [];
  const byService = new Map();

  for (const row of rows) {
    const service = normalizeServiceName(row?.service || row?.service_type);
    const current = byService.get(service) || {
      service,
      revenue: 0,
      recordsCount: 0,
    };

    current.revenue += Number(row?.revenue ?? row?.total ?? row?.amount ?? 0);
    current.recordsCount += Number(
      row?.records_count ?? row?.ops ?? row?.count ?? 0
    );
    byService.set(service, current);
  }

  return Object.keys(SERVICE_META).map((service) => ({
    service,
    revenue: byService.get(service)?.revenue || 0,
    recordsCount: byService.get(service)?.recordsCount || 0,
  }));
}

function normalizeTopHorses(data) {
  const rows = Array.isArray(data?.topHorses)
    ? data.topHorses
    : Array.isArray(data?.top_horses)
    ? data.top_horses
    : [];

  return rows.map((row, index) => ({
    id: row?.horse_id ?? row?.id ?? index,
    horseName:
      row?.horse_name ||
      row?.horseName ||
      row?.name_arabic ||
      row?.name ||
      '-',
    ownerName: row?.owner_name || row?.ownerName || row?.owner || '-',
    total: Number(row?.total ?? row?.total_spend ?? row?.amount ?? 0),
  }));
}

function normalizeTopOwners(data) {
  const rows = Array.isArray(data?.topOwners) ? data.topOwners : [];
  return rows.map((row, index) => ({
    id: row?.owner_id ?? index,
    ownerName: row?.owner_name || '-',
    horsesCount: Number(row?.horses_count || 0),
    total: Number(row?.total || 0),
  }));
}

function normalizeTopItems(data) {
  const rows = Array.isArray(data?.topItems) ? data.topItems : [];
  return rows.map((row, index) => ({
    id: `${row?.item_type || 'item'}-${row?.item_name || index}`,
    type: row?.item_type || '-',
    name: row?.item_name || '-',
    quantity: Number(row?.quantity || 0),
    revenue: Number(row?.revenue || 0),
  }));
}

function normalizeMonthlyTrend(data) {
  const rows = Array.isArray(data?.monthlyTrend) ? data.monthlyTrend : [];
  return rows.map((row) => ({
    bucket: row?.bucket || '-',
    revenue: Number(row?.revenue || 0),
  }));
}

function toDateOnly(value) {
  if (!value) return '';
  return formatDateDisplay(value, '');
}

function normalizeCoverage(data) {
  const rows = Array.isArray(data?.dataCoverage) ? data.dataCoverage : [];
  return rows.reduce((acc, row) => {
    const service = normalizeServiceName(row?.service);
    acc[service] = {
      recordsCount: Number(row?.records_count || 0),
      firstDate: toDateOnly(row?.first_date),
      lastDate: toDateOnly(row?.last_date),
    };
    return acc;
  }, {});
}

function getServiceLabel(t, service) {
  const meta = SERVICE_META[service];
  return t(meta?.labelKey || service) || meta?.fallback || service;
}

function getItemTypeLabel(t, type) {
  const labels = {
    vet_procedure: t('vet_procedure') || 'إجراء عيادة',
    vet_medication: t('vet_medication') || 'دواء عيادة',
    farrier_procedure: t('farrier_procedure') || 'إجراء بيطار',
    farrier_medication: t('farrier_medication') || 'دواء بيطار',
    accommodation_type: t('accommodation_type') || 'نوع إقامة',
    competition_finance: t('competition_finance') || 'Competition finance',
  };
  return labels[type] || type;
}

function normalizeCompetitionFinance(data) {
  const source = data?.competitionFinance || {};
  return {
    expenses: Number(source.expenses || 0),
    revenue: Number(source.revenue || 0),
    net: Number(source.net || 0),
    excludedExpenses: Number(source.excludedExpenses || 0),
    excludedRevenue: Number(source.excludedRevenue || 0),
    rows: Array.isArray(source.rows) ? source.rows : [],
  };
}

function MiniBar({ value, max }) {
  const pct = max > 0 ? Math.max(4, Math.min(100, (value / max) * 100)) : 0;
  return (
    <Box
      sx={{
        height: 7,
        borderRadius: 999,
        backgroundColor: COLORS.cardSoft,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          width: `${pct}%`,
          height: '100%',
          borderRadius: 999,
          backgroundColor: COLORS.gold,
        }}
      />
    </Box>
  );
}

function ResponsiveGrid({ columns = 3, children }) {
  const mediumColumns = columns >= 5 ? 3 : columns;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: columns >= 4 ? 'repeat(2, minmax(0, 1fr))' : '1fr',
          md: `repeat(${mediumColumns}, minmax(0, 1fr))`,
          lg: `repeat(${columns}, minmax(0, 1fr))`,
        },
        gap: 2,
        alignItems: 'stretch',
      }}
    >
      {children}
    </Box>
  );
}

export default function FinanceSummaryPage() {
  const { t } = useTranslation();

  const [preset, setPreset] = useState('month');
  const [range, setRange] = useState(() => getPresetRange('month'));
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const fetchSummary = useCallback(async () => {
    if (!range.from || !range.to) {
      setErrorMsg(t('invalid_date_range') || 'الفترة غير مكتملة');
      return;
    }

    if (range.from > range.to) {
      setErrorMsg(t('invalid_date_range') || 'تاريخ البداية بعد تاريخ النهاية');
      return;
    }

    setLoading(true);
    setErrorMsg('');

    try {
      const { data } = await api.get('/accounting/finance/summary-inside', {
        params: { from: range.from, to: range.to, bucket: 'monthly' },
      });
      setSummary(data || null);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to load finance summary';
      setErrorMsg(String(msg));
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [range, t]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const totals = useMemo(() => normalizeTotals(summary), [summary]);
  const topHorses = useMemo(() => normalizeTopHorses(summary), [summary]);
  const topOwners = useMemo(() => normalizeTopOwners(summary), [summary]);
  const topItems = useMemo(() => normalizeTopItems(summary), [summary]);
  const monthlyTrend = useMemo(() => normalizeMonthlyTrend(summary), [summary]);
  const coverage = useMemo(() => normalizeCoverage(summary), [summary]);
  const competitionFinance = useMemo(() => normalizeCompetitionFinance(summary), [summary]);

  const grandTotal = useMemo(() => {
    if (summary?.grandTotal != null) return Number(summary.grandTotal || 0);
    return totals.reduce((sum, row) => sum + row.revenue, 0);
  }, [summary, totals]);

  const recordsCount = useMemo(() => {
    if (summary?.recordsCount != null) return Number(summary.recordsCount || 0);
    return totals.reduce((sum, row) => sum + row.recordsCount, 0);
  }, [summary, totals]);

  const topHorseTotal = topHorses[0]?.total || 0;
  const maxOwnerTotal = Math.max(...topOwners.map((row) => row.total), 0);
  const maxItemRevenue = Math.max(...topItems.map((row) => row.revenue), 0);
  const maxMonthlyRevenue = Math.max(
    ...monthlyTrend.map((row) => row.revenue),
    0
  );
  const revenueCards = useMemo(
    () => totals.filter((row) => row.service !== 'competitions'),
    [totals]
  );

  const emptyServicesWithHistory = useMemo(
    () =>
      totals
        .filter((row) => Number(row.revenue || 0) === 0)
        .map((row) => ({
          ...row,
          coverage: coverage[row.service],
        }))
        .filter((row) => Number(row.coverage?.recordsCount || 0) > 0),
    [totals, coverage]
  );

  const coverageItems = useMemo(
    () =>
      emptyServicesWithHistory.map((row) => {
        const label = getServiceLabel(t, row.service);
        const first = row.coverage?.firstDate || '-';
        const last = row.coverage?.lastDate || '-';
        return { key: `${row.service}-${first}-${last}`, label, first, last };
      }),
    [emptyServicesWithHistory, t]
  );

  const handlePresetChange = (event) => {
    const next = event.target.value;
    setPreset(next);
    setRange(getPresetRange(next));
  };

  const handleRangeChange = (nextRange) => {
    setPreset('custom');
    setRange(nextRange);
  };

  return (
    <Box
      className="smart-page-container"
      sx={{
        width: '100%',
        maxWidth: 'var(--app-page-max)',
        mx: 'auto',
        bgcolor: COLORS.page,
        overflowX: 'hidden',
      }}
    >
      <Stack spacing={2}>
        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            p: { xs: 1.75, md: 2 },
            background: 'linear-gradient(135deg, #fffdf8 0%, #f4efe5 100%)',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            sx={{ gap: 2 }}
          >
            <Box sx={{ minWidth: 240 }}>
              <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>
                {t('finance_summary') || 'ملخص المحاسبة'}
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.muted }}>
                {t('inside_horses_finance') ||
                  'إيرادات الخدمات للخيل الداخلي حسب الفترة المحددة'}
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                select
                size="small"
                label={t('period') || 'الفترة'}
                value={preset}
                onChange={handlePresetChange}
                sx={{ minWidth: { xs: '100%', sm: 170 } }}
              >
                <MenuItem value="today">{t('today') || 'اليوم'}</MenuItem>
                <MenuItem value="month">{t('current_month') || 'الشهر الحالي'}</MenuItem>
                <MenuItem value="last30">{t('last_30_days') || 'آخر 30 يوم'}</MenuItem>
                <MenuItem value="year">{t('current_year') || 'السنة الحالية'}</MenuItem>
                <MenuItem value="custom">{t('custom') || 'مخصص'}</MenuItem>
              </TextField>

              <DateRange value={range} onChange={handleRangeChange} t={t} />

              <Tooltip title={t('refresh') || 'تحديث'}>
                <span>
                  <Button
                    variant="contained"
                    onClick={fetchSummary}
                    disabled={loading}
                    startIcon={
                      loading ? <CircularProgress size={16} /> : <RefreshIcon />
                    }
                    sx={{
                      height: 40,
                      whiteSpace: 'nowrap',
                      bgcolor: COLORS.gold,
                      fontWeight: 900,
                      '&:hover': { bgcolor: COLORS.goldHover },
                    }}
                  >
                    {t('refresh') || 'تحديث'}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </Paper>

        {errorMsg ? <Alert severity="error">{errorMsg}</Alert> : null}

        {!errorMsg && coverageItems.length > 0 ? (
          <Alert
            severity="info"
            sx={{
              alignItems: 'flex-start',
              border: `1px solid ${COLORS.line}`,
              borderRadius: 2,
              bgcolor: COLORS.card,
              '& .MuiAlert-message': { width: '100%' },
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              flexWrap="wrap"
            >
              <Typography variant="body2" sx={{ mr: 1 }}>
                {t('no_records_for_services_in_range') ||
                  'لا توجد سجلات داخل الفترة المحددة لبعض الخدمات، رغم وجود بيانات لها خارج هذه الفترة:'}
              </Typography>
              <Stack direction="row" gap={1} flexWrap="wrap">
                {coverageItems.map((item) => (
                  <Chip
                    key={item.key}
                    size="small"
                    variant="outlined"
                    label={
                      <>
                        {item.label}: <span className="date-ltr">{item.first}</span> -{' '}
                        <span className="date-ltr">{item.last}</span>
                      </>
                    }
                  />
                ))}
              </Stack>
            </Stack>
          </Alert>
        ) : null}

        <ResponsiveGrid columns={3}>
          <Box>
            <KPI
              title={t('total_revenue') || 'إجمالي الإيرادات'}
              value={`${fmtCompactMoney(grandTotal, t)} ${currencyLabel(t)}`}
            />
          </Box>
          <Box>
            <KPI
              title={t('records_count') || 'عدد العمليات'}
              value={fmtInt(recordsCount)}
            />
          </Box>
          <Box>
            <KPI
              title={t('top_horse_spend') || 'أعلى إنفاق لخيل'}
              value={`${fmtCompactMoney(topHorseTotal, t)} ${currencyLabel(t)}`}
            />
          </Box>
        </ResponsiveGrid>

        <ResponsiveGrid columns={5}>
          {revenueCards.map((row) => {
            const share = grandTotal > 0 ? (row.revenue / grandTotal) * 100 : 0;
            return (
              <Paper
                key={row.service}
                elevation={0}
                sx={{
                  p: 2.25,
                  ...cardSx,
                  height: '100%',
                  minHeight: 132,
                }}
              >
                <Stack spacing={1.25} sx={{ height: '100%' }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>
                      {getServiceLabel(t, row.service)}
                    </Typography>
                    <TrendingUpIcon fontSize="small" color="action" />
                  </Stack>
                  <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>
                    {fmtMoney(row.revenue)} {currencyLabel(t)}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip
                      size="small"
                      icon={<ReceiptLongIcon />}
                      label={`${fmtInt(row.recordsCount)} ${
                        t('records') || 'عملية'
                      }`}
                    />
                    <Chip size="small" label={`${share.toFixed(1)}%`} />
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </ResponsiveGrid>

        <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.75 }}>
            <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>
              {t('competition_finance') || 'Competition finance'}
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.muted }}>
              {t('competition_finance_hint') ||
                'Only stable-paid expenses and stable-received prize income are included in totals.'}
            </Typography>
          </Box>
          <Divider />
          <Box sx={{ p: 2 }}>
            <ResponsiveGrid columns={3}>
              <KPI
                title={t('competition_expenses') || 'Competition expenses'}
                value={`${fmtMoney(competitionFinance.expenses)} ${currencyLabel(t)}`}
              />
              <KPI
                title={t('competition_revenue') || 'Competition revenue'}
                value={`${fmtMoney(competitionFinance.revenue)} ${currencyLabel(t)}`}
              />
              <KPI
                title={t('net_competition_result') || 'Net competition result'}
                value={`${fmtMoney(competitionFinance.net)} ${currencyLabel(t)}`}
              />
            </ResponsiveGrid>
            <TableContainer sx={{ mt: 2, width: '100%' }}>
              <Table size="small" sx={{ minWidth: 980 }}>
                <TableHead
                  sx={{
                    '& .MuiTableCell-root': {
                      backgroundColor: COLORS.gold,
                      color: '#fff',
                      borderBottom: 0,
                      py: 1.1,
                    },
                  }}
                >
                  <TableRow>
                    <TableCell>{t('date') || 'Date'}</TableCell>
                    <TableCell>{t('competition') || 'Competition'}</TableCell>
                    <TableCell>{t('horse') || 'Horse'}</TableCell>
                    <TableCell>{t('owner') || 'Owner'}</TableCell>
                    <TableCell>{t('transaction_type') || 'Type'}</TableCell>
                    <TableCell>{t('direction') || 'Direction'}</TableCell>
                    <TableCell align="right">{t('amount') || 'Amount'}</TableCell>
                    <TableCell>{t('included_in_stable_finance') || 'Included'}</TableCell>
                    <TableCell>{t('review_status') || 'Review status'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {competitionFinance.rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        {t('no_competition_financial_records_found') ||
                          'No competition financial records found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    competitionFinance.rows.slice(0, 10).map((row) => (
                      <TableRow key={row.transaction_id} hover>
                        <TableCell><span className="date-ltr">{toDateOnly(row.transaction_date)}</span></TableCell>
                        <TableCell>{row.competition_name || row.competition_name_ar || '-'}</TableCell>
                        <TableCell>{row.horse_name || row.horse_name_arabic || '-'}</TableCell>
                        <TableCell>{row.owner_name || row.owner_name_ar || '-'}</TableCell>
                        <TableCell>{t(String(row.transaction_type || '').toLowerCase()) || row.transaction_type}</TableCell>
                        <TableCell>{t(String(row.direction || '').toLowerCase()) || row.direction}</TableCell>
                        <TableCell align="right">{fmtMoney(row.amount)} {currencyLabel(t)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={Number(row.include_in_stable_finance) ? (t('yes') || 'Yes') : (t('no') || 'No')}
                          />
                        </TableCell>
                        <TableCell>{t(String(row.accountant_review_status || '').toLowerCase()) || row.accountant_review_status || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {(competitionFinance.excludedExpenses || competitionFinance.excludedRevenue) ? (
              <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: COLORS.muted }}>
                {t('excluded_from_stable_finance') || 'Excluded from stable finance'}:{' '}
                {fmtMoney(competitionFinance.excludedExpenses + competitionFinance.excludedRevenue)} {currencyLabel(t)}
              </Typography>
            ) : null}
          </Box>
        </Paper>

        <ResponsiveGrid columns={3}>
          <Paper
            elevation={0}
            sx={{
              ...cardSx,
              p: 2,
              height: '100%',
            }}
          >
            <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900, mb: 1 }}>
              {t('top_owners_by_revenue') || 'أعلى الملاك بالإيراد'}
            </Typography>
            <Stack spacing={1.25}>
              {topOwners.length === 0 ? (
                <Typography color="text.secondary">
                  {t('no_data') || 'لا توجد بيانات'}
                </Typography>
              ) : (
                topOwners.slice(0, 5).map((row) => (
                  <Box key={row.id}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Typography sx={{ fontWeight: 700 }} noWrap>
                        {row.ownerName}
                      </Typography>
                      <Typography sx={{ fontWeight: 800 }} noWrap>
                        {fmtMoney(row.total)} {currencyLabel(t)}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ mb: 0.5 }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {fmtInt(row.horsesCount)} {t('horses') || 'خيول'}
                      </Typography>
                    </Stack>
                    <MiniBar value={row.total} max={maxOwnerTotal} />
                  </Box>
                ))
              )}
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              ...cardSx,
              p: 2,
              height: '100%',
            }}
          >
            <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900, mb: 1 }}>
              {t('top_items_by_revenue') || 'أعلى البنود بالإيراد'}
            </Typography>
            <Stack spacing={1.25}>
              {topItems.length === 0 ? (
                <Typography color="text.secondary">
                  {t('no_data') || 'لا توجد بيانات'}
                </Typography>
              ) : (
                topItems.slice(0, 5).map((row) => (
                  <Box key={row.id}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Typography sx={{ fontWeight: 700 }} noWrap>
                        {row.name}
                      </Typography>
                      <Typography sx={{ fontWeight: 800 }} noWrap>
                        {fmtMoney(row.revenue)} {currencyLabel(t)}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ mb: 0.5 }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {getItemTypeLabel(t, row.type)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmtInt(row.quantity)} {t('quantity') || 'كمية'}
                      </Typography>
                    </Stack>
                    <MiniBar value={row.revenue} max={maxItemRevenue} />
                  </Box>
                ))
              )}
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              ...cardSx,
              p: 2,
              height: '100%',
            }}
          >
            <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900, mb: 1 }}>
              {t('monthly_revenue_trend') || 'اتجاه الإيرادات شهريًا'}
            </Typography>
            <Stack spacing={1.25}>
              {monthlyTrend.length === 0 ? (
                <Typography color="text.secondary">
                  {t('no_data') || 'لا توجد بيانات'}
                </Typography>
              ) : (
                monthlyTrend.slice(-6).map((row) => (
                  <Box key={row.bucket}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ mb: 0.5 }}
                    >
                      <Typography sx={{ fontWeight: 700 }}>
                        {row.bucket}
                      </Typography>
                      <Typography sx={{ fontWeight: 800 }}>
                        {fmtMoney(row.revenue)} {currencyLabel(t)}
                      </Typography>
                    </Stack>
                    <MiniBar value={row.revenue} max={maxMonthlyRevenue} />
                  </Box>
                ))
              )}
            </Stack>
          </Paper>
        </ResponsiveGrid>

        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ px: 2, py: 1.75 }}>
            <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>
              {t('top_spending_horses') || 'أكثر الخيول إنفاقًا'}
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.muted }}>
              {t('top_horses_hint') ||
                'مرتبة حسب مجموع العيادة والبيطار والنقل والسكن داخل الفترة'}
            </Typography>
          </Box>
          <Divider />

          <TableContainer sx={{ px: 0, width: '100%' }}>
            <Table size="small" sx={{ minWidth: 860 }}>
              <TableHead
                sx={{
                  '& .MuiTableCell-root': {
                    backgroundColor: COLORS.gold,
                    color: '#fff',
                    borderBottom: 0,
                    py: 1.1,
                  },
                }}
              >
                <TableRow>
                  <TableCell sx={{ fontWeight: 800, width: 70 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>
                    {t('horse') || 'الخيل'}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>
                    {t('owner') || 'المالك'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, width: 180 }}>
                    {t('total') || 'الإجمالي'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, width: 120 }}>
                    {t('share') || 'النسبة'}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : topHorses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      {t('no_data') || 'لا توجد بيانات ضمن الفترة المحددة'}
                    </TableCell>
                  </TableRow>
                ) : (
                  topHorses.map((row, index) => {
                    const share =
                      grandTotal > 0 ? (row.total / grandTotal) * 100 : 0;

                    return (
                      <TableRow key={`${row.id}-${index}`} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{row.horseName}</TableCell>
                        <TableCell>{row.ownerName}</TableCell>
                        <TableCell align="right">
                          {fmtMoney(row.total)} {currencyLabel(t)}
                        </TableCell>
                        <TableCell align="right">{share.toFixed(1)}%</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Stack>
    </Box>
  );
}
